<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import BetaFileCard from '@/components/beta-dashboard/molecules/BetaFileCard.vue'
import BetaFileDialog from '@/components/beta-dashboard/organisms/BetaFileDialog.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useSiteStore } from '@/stores/site'
import type { BetaFile, BetaFileInput } from '@/types/beta-dashboard'

const betaStore = useBetaDashboardStore()
const siteStore = useSiteStore()

const dialogOpen = ref(false)
const dialog = ref<InstanceType<typeof BetaFileDialog> | null>(null)
const uploading = ref(false)
const saving = ref(false)
const deletingIds = ref<number[]>([])

onBeforeMount(() => betaStore.loadFiles())

const siteUrl = computed(() => {
    if (!siteStore.url) return ''
    return /^https?:\/\//i.test(String(siteStore.url))
        ? String(siteStore.url)
        : `https://${siteStore.url}`
})

const closeDialog = () => {
    if (uploading.value || saving.value) return
    dialogOpen.value = false
}

const uploadFile = async (file: File) => {
    if (uploading.value) return

    uploading.value = true
    try {
        const uploadedFile = await betaStore.uploadFile(file)
        dialog.value?.applyUploadedFile(uploadedFile)
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível enviar o arquivo.',
            'error',
        )
    } finally {
        uploading.value = false
    }
}

const saveFile = async (file: BetaFileInput) => {
    if (saving.value) return

    saving.value = true
    const toastId: ToastId = notification.loading('Adicionando arquivo…')
    try {
        await betaStore.addFile(file)
        notification.update(toastId, 'Arquivo adicionado com sucesso.', 'success')
        dialogOpen.value = false
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível adicionar o arquivo.',
            'error',
        )
    } finally {
        saving.value = false
    }
}

const copyUrl = async (url: string) => {
    try {
        await navigator.clipboard.writeText(url)
        notification.simple('Link copiado para a área de transferência.', 'success')
    } catch {
        notification.simple('Erro ao copiar link para a área de transferência.', 'error')
    }
}

const removeFile = async (file: BetaFile) => {
    const confirmed = await modal.confirmation(
        'Excluir arquivo?',
        `“${file.name}” deixará de estar disponível para download.`,
        {
            type: 'danger',
            textConfirm: 'Sim, excluir',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed || deletingIds.value.includes(file.id)) return

    deletingIds.value = [...deletingIds.value, file.id]
    const toastId: ToastId = notification.loading('Excluindo arquivo…')
    try {
        await betaStore.removeFile(file.id)
        notification.update(toastId, 'Arquivo excluído com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível excluir o arquivo.',
            'error',
        )
    } finally {
        deletingIds.value = deletingIds.value.filter((id) => id !== file.id)
    }
}
</script>

<template>
    <div class="beta-files">
        <div class="beta-files__heading">
            <div>
                <p>Arquivos</p>
                <h2>Downloads do seu site</h2>
                <span>Envie documentos e compartilhe uma URL pública com seus visitantes.</span>
            </div>
            <button type="button" @click="dialogOpen = true">+ Novo arquivo</button>
        </div>

        <aside class="beta-files__notice">
            <strong>Arquivos de até 30 MB</strong>
        </aside>

        <div v-if="betaStore.loadingFiles" class="beta-files__state">
            Carregando arquivos…
        </div>

        <div v-else-if="betaStore.filesError" class="beta-files__state is-error">
            <span>{{ betaStore.filesError }}</span>
            <button type="button" @click="betaStore.loadFiles()">Tentar novamente</button>
        </div>

        <div v-else-if="!betaStore.files.length" class="beta-files__state">
            <strong>Nenhum arquivo disponível</strong>
            <span>Envie um documento para gerar seu link público.</span>
            <button type="button" @click="dialogOpen = true">Adicionar arquivo</button>
        </div>

        <div v-else class="beta-files__grid">
            <BetaFileCard
                v-for="file in betaStore.files"
                :key="file.id"
                :file="file"
                :deleting="deletingIds.includes(file.id)"
                @copy="copyUrl"
                @remove="removeFile"
            />
        </div>

        <BetaFileDialog
            ref="dialog"
            :open="dialogOpen"
            :site-url="siteUrl"
            :uploading="uploading"
            :saving="saving"
            @close="closeDialog"
            @upload="uploadFile"
            @save="saveFile"
        />
    </div>
</template>

<style scoped>
.beta-files {
    display: grid;
    gap: 16px;
}

.beta-files__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf1f6;
}

.beta-files__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-files__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-files__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-files__heading > button,
.beta-files__state button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 14px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-files__notice {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid color-mix(in srgb, var(--main-color) 15%, #e1e7ef);
    border-radius: 13px;
    background: color-mix(in srgb, var(--main-color) 4%, #f9fbfd);
    padding: 10px 12px;
}

.beta-files__notice strong {
    flex: none;
    color: #314965;
    font-size: 12px;
    font-weight: 850;
}

.beta-files__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.beta-files__state {
    display: grid;
    min-height: 180px;
    place-content: center;
    justify-items: center;
    gap: 6px;
    border: 1px dashed #dbe3ed;
    border-radius: 17px;
    background: #f9fbfd;
    padding: 20px;
    color: #7e8da2;
    font-size: 12px;
    text-align: center;
}

.beta-files__state strong {
    color: #304863;
    font-size: 14px;
}

.beta-files__state button {
    margin-top: 5px;
}

.beta-files__state.is-error {
    color: #a34e4e;
}

@media (max-width: 700px) {
    .beta-files__heading,
    .beta-files__notice {
        align-items: flex-start;
        flex-direction: column;
    }

    .beta-files__grid {
        grid-template-columns: 1fr;
    }
}
</style>
